/**
 * @description 腾讯视频web端代码实现
 * @author aprilcai
 */

import { resultCode, resultMessage, wrapResult } from '../module/entity';
import {
  $getPayUrl,
  $commonGetUserInfo,
  $getNetworkState,
  $getAvatar,
  $openPayVipView,
  $share,
  $toast,
} from './pc-web-common-method';
import { getCookie, loadH5LoginModule, callOnce } from '../module/util';
import { isMobile, isTenvideoWeb, isQQ, isWeixin, isWeiBo } from '../module/ua';

// 初始化移动端h5登录
if (isMobile && isTenvideoWeb) {
  loadH5LoginModule().then((h5Login) => {
    h5Login.init();
  });
}

// 当前webview是否支持
export function support() {
  return isTenvideoWeb;
}

// 登录
export async function login(): Promise<IResult> {
  const { txv } = window;
  if (isMobile) {
    const h5Login = await loadH5LoginModule();
    let url = '';
    try {
      url = window.top.location.href;
    } catch (e) {
      //
    }
    h5Login.openLogin({ url });
    return wrapResult(resultCode.ok, resultMessage.ok);
  }

  // 腾讯视频站内
  if (typeof txv !== 'undefined' && typeof txv.login !== 'undefined') {
    return new Promise((resolve) => {
      txv.login.openLogin();
      window.txv.login.addLoginCancelCallback(callOnce(() => {
        resolve({
          code: resultCode.cancel,
          msg: resultMessage.cancel,
          result: {},
        });
      }));
      window.txv.login.addLoginCallback(callOnce(() => {
        resolve({
          code: 0,
          msg: 'ok',
          result: {},
        });
      }));
    });
  }
  // 其他场景
  return wrapResult(resultCode.platformNotSupport, `${resultMessage.platformNotSupport}(login)`);
}

// 退出登录
export async function logout(): Promise<IResult> {
  const { txv } = window;
  if (isMobile) {
    const h5Login = await loadH5LoginModule();
    h5Login.logout();
  } else if (typeof txv !== 'undefined' && typeof txv.login !== 'undefined') {
    txv.login.logout();
  }
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 是否登录
export async function isLogin(): Promise<IResult> {
  const { txv } = window;
  return new Promise((resolve) => {
    if (isMobile) {
      loadH5LoginModule().then((h5Login) => {
        h5Login.onInit(() => {
          resolve({
            code: resultCode.ok,
            msg: resultMessage.ok,
            result: {
              isLogin: h5Login.isLogin(),
            },
          });
        });
      });
    } else if (typeof txv !== 'undefined' && typeof txv.login !== 'undefined') {
      resolve({
        code: resultCode.ok,
        msg: resultMessage.ok,
        result: {
          isLogin: txv.login.isLogin(),
        },
      });
    } else {
      resolve({
        code: resultCode.notSupport,
        msg: `${resultMessage.notSupport}: no login module found`,
        result: {
          isLogin: false,
        },
      });
    }
  });
}

// 获取用户信息
export async function getUserInfo(param: { data: any; isTest: boolean }): Promise<IResult> {
  const isLoginRes = await isLogin();
  const { result } = isLoginRes;
  const isLoginResult = result;
  if (isLoginResult) {
    const type = getCookie('main_login');
    const avatar = await $getAvatar(type);
    const userInfoRes = await $commonGetUserInfo(param);
    let userInfo = userInfoRes.result;
    if (userInfoRes.code === resultCode.ok) {
      userInfo.baseInfo.avatar = avatar;
      userInfo.type = type;
    } else {
      userInfo = {
        appid: '',
        openid: '',
        token: '',
        baseInfo: {
          nickname: '',
          vip: false,
          avatar: '',
        },
        type,
      };
    }
    return Promise.resolve(wrapResult(userInfoRes.code, userInfoRes.msg || resultMessage.ok, userInfo));
  }
  return Promise.resolve(wrapResult(resultCode.error, `${resultMessage.error}(no login)`));
}

// 用户设备id
export async function getDeviceId(): Promise<IResult> {
  return wrapResult(resultCode.ok, resultMessage.ok, {
    id: getCookie('video_guid'),
  });
}

// 获取音量
export async function getVolume(): Promise<IResult> {
  const globalPlayerGetVolume = window?.globalPlayerGetVolume || { globalPlayerGetVolume: () => 0 };
  const volume = globalPlayerGetVolume();
  return wrapResult(resultCode.ok, resultMessage.ok, {
    volume,
  });
}

// 设置音量
export async function setVolume(param: { volume: number }): Promise<IResult> {
  const globalPlayerSetVolume = window?.globalPlayerSetVolume || {
    globalPlayerSetVolume: () => {},
  };
  globalPlayerSetVolume(param.volume);
  return wrapResult(resultCode.ok, resultMessage.ok, {
    volume: param.volume,
  });
}

// 获取网络状态
export async function getNetworkState(): Promise<IResult> {
  return $getNetworkState();
}

// 打开webview
export async function openWebview(param: { url: string }): Promise<IResult> {
  window.open(decodeURIComponent(param.url));
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 打开支付页
export async function openPayPage(param: {
  appid: string;
  actid: string;
  orderid: string;
  needpay: string;
  close: number;
  ru: string;
  title: string;
  sandbox: number;
  autopay: number;
}): Promise<IResult> {
  const { result } = (await $getPayUrl(param)) as any;
  window.open(result.payUrl);
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 刷新标题
export async function refreshTitle(param: { title: string }): Promise<IResult> {
  window.top.document.title = param.title;
  // QQ里面刷新标题
  if (window.mqq?.ui) {
    window.mqq.ui.refreshTitle();
  }
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 分享
export async function share(param: {
  title?: string;
  subTitle?: string;
  url?: string;
  imageUrl?: string;
  sharePageTitle?: string;
  sharePageLaunchUrl?: string;
  pcShareImageUrl?: string;
  pcShareImageWidth?: string;
  pcShareImageHeight?: string;
}): Promise<IResult> {
  return $share(param);
}

// base64字符串来发起请求保存本地保存
function downloadImage(base64Str: string) {
  if (base64Str) {
    const match = /^data:image\/(\w+);base64,/.exec(base64Str.substr(0, 30));
    const format = match.length > 1 ? match[1] : 'png';
    const a = document.createElement('a');
    a.href = base64Str;
    a.style.display = 'none';
    a.download = `image_${new Date().getTime()}.${format}`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
  }
}

// 保存图片
export async function saveImage(param: { url: string }): Promise<IResult> {
  return new Promise((resolve) => {
    const { url } = param;
    if (!url) {
      resolve({
        code: resultCode.error,
        msg: `${resultMessage.error}: no url`,
        result: {},
      });
      return;
    }

    // 手机上弹出一个图片预览浮层，用长按保存的方式来实现
    if (isMobile && (isQQ || isWeixin || isWeiBo)) {
      const img = new Image();
      const id = `bridge_save_image_${new Date().getTime()}`;
      img.id = id;
      img.src = param.url;
      img.style.width = '80vw';
      img.style.position = 'fixed';
      img.style.zIndex = '100';
      img.style.left = '50%';
      img.style.top = '50%';
      img.style.transform = 'translate(-50%, -50%)';
      img.addEventListener('touchstart', (event) => {
        event.stopPropagation();
      });
      const container = document.createElement('div');
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.left = '0';
      container.style.top = '0';
      container.style.backgroundColor = 'rgba(0,0,0,0.8)';
      container.style.position = 'fixed';
      container.style.zIndex = '99';
      container.appendChild(img);
      let topBody = document.body;
      try {
        topBody = window.top.document.body;
      } catch (e) {
        // ignore
      }
      topBody.appendChild(container);
      container.addEventListener('click', (event) => {
        if (!/img/i.test((event.target as HTMLElement).tagName)) {
          container.remove();
        }
      });
      resolve({
        code: resultCode.ok,
        msg: resultMessage.ok,
        result: {},
      });
      return;
    }

    // PC 上使用下载图片的方式来实现
    const xhr = new XMLHttpRequest();
    xhr.open('get', param.url);
    xhr.responseType = 'blob';
    xhr.addEventListener('load', () => {
      const fs = new FileReader();
      fs.addEventListener('load', (e) => {
        downloadImage(e.target.result.toString());
        resolve({
          code: 0,
          msg: 'ok',
          result: {},
        });
      });
      fs.readAsDataURL(xhr.response);
      resolve({
        code: resultCode.ok,
        msg: resultMessage.ok,
        result: {},
      });
    });
    xhr.addEventListener('error', () => {
      resolve({
        code: resultCode.error,
        msg: `${resultMessage.error}: network error (${xhr.status})`,
        result: {},
      });
    });
    xhr.send();
  });
}

// 写日志
export async function writeLog(param: {
  appid: string;
  content: string;
  isReportServer: boolean;
  isConsoleLog: boolean;
}): Promise<IResult> {
  const { appid, content, isConsoleLog } = param;
  if (isConsoleLog) {
    const logContent = `bridge-helper: ${new Date().toLocaleString()}, appid=${appid}, content=${content}`;
    window.console.log(logContent);
  }
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 打开VIP开通页/浮层
export async function openPayVipView(param: {
  actid?: string;
  nt?: string;
  n?: string;
  sp?: string;
  ptag?: string;
  aid?: string;
  dsp?: string;
  tab?: string;
  dnp?: string;
  unwx?: string;
  url?: string;
}) {
  return $openPayVipView(param);
}

// toast
export async function toast(content: string) {
  return $toast(content);
}
